import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useAccount } from 'wagmi'
import { useNoBnbShown } from 'components/NoBnbGuide/presence'
import { ApiError, Profile, api, loadSession } from './api'

const KEY = 'cadinu.social.later'
/**
 * Pages the bar never appears on: /u shows the steps itself; /swap keeps its commit button and the No BNB guide at the bottom of the form,
 * which a fixed bar would cover while scrolling (manager review of Stage r166 at 390 px, 2026-10-07).
 */
const HIDDEN_ON = ['/u', '/swap']
/** Bar height, the same number the in-flow spacer uses so no page content can end under the bar. */
const BAR_H = 52
/** The uikit BottomNav on phones: 44px items + 2 x 5px padding + 1px border; the safe-area inset is added in CSS. */
const PHONE_NAV_H = 56

const Bar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(${PHONE_NAV_H}px + env(safe-area-inset-bottom));
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: ${BAR_H}px;
  padding: 6px 10px 6px 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-top: 1px solid rgba(127, 212, 232, 0.45);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.25);
  color: ${({ theme }) => theme.colors.text};
  ${({ theme }) => theme.mediaQueries.md} {
    bottom: 0;
    padding-left: 24px;
    padding-right: 16px;
  }
`
const Copy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 18px;
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
  }
`
const Title = styled.span`
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  .lg {
    display: none;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    .lg {
      display: inline;
    }
    .sm {
      display: none;
    }
  }
`
const Sub = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textSubtle};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  .lg {
    display: none;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 14px;
    line-height: 18px;
    .lg {
      display: inline;
    }
    .sm {
      display: none;
    }
  }
`
const View = styled.a`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 0;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  color: #0b1220;
  text-decoration: none;
  background: linear-gradient(135deg, #7fd4e8, #9a7bff);
  box-shadow: 0 8px 20px -10px rgba(127, 212, 232, 0.9);
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 2px;
  }
`
const Close = styled.button`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(127, 212, 232, 0.35);
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
`
/** In-flow spacer: pages stay fully scrollable above the bar (bar height, plus the gap between the phone nav and the Menu's own 44px margin). */
const Spacer = styled.div`
  height: ${BAR_H + 12}px;
  ${({ theme }) => theme.mediaQueries.md} {
    height: ${BAR_H}px;
  }
`

/**
 * Right after a wallet connects (a wallet without a CADINU profile only): "Your CADINU profile is ready" with a link to it. Since 2026-10-07
 * (Bez) every wallet has an automatic profile page built from its public activity, so the bar no longer asks for a signature; it names the
 * next step (claiming, one free signature, done on the profile page). A compact bar pinned to the bottom of the viewport (above the bottom
 * nav on phones), never over the page's content thanks to the in-flow spacer. "Not now" is remembered per wallet in localStorage.
 * Never shown on the /u pages, which show the steps themselves.
 */
export default function AutoJoin() {
  const { address } = useAccount()
  const { pathname } = useRouter()
  const noBnb = useNoBnbShown()
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (!address || HIDDEN_ON.some((p) => pathname.startsWith(p))) return setShow(false)
    let later: string[] = []
    try {
      later = JSON.parse(window.localStorage.getItem(KEY) || '[]')
    } catch {
      later = []
    }
    if (loadSession(address) || later.includes(address.toLowerCase())) return setShow(false)
    // 2026-10-02 (Bez): a wallet that already has a CADINU profile is never asked to "sign in"; the banner is only the
    // create action for a wallet without one (the public endpoint answers {state: 'none'}, HTTP 404)
    let live = true
    api<Profile>(`profiles/${address}`)
      .then((p) => live && setShow(p.state === 'none'))
      .catch((e) => live && setShow(e instanceof ApiError && e.status === 404))
    return () => {
      live = false
    }
  }, [address, pathname])
  // a banner never covers content: it steps aside while a "No BNB" guide is on the page (it sits right above a transaction button)
  if (!show || !address || noBnb) return null
  const dismiss = () => {
    try {
      const later = JSON.parse(window.localStorage.getItem(KEY) || '[]')
      window.localStorage.setItem(KEY, JSON.stringify([...later, address.toLowerCase()].slice(-50)))
    } catch {
      /* ignore */
    }
    setShow(false)
  }
  return (
    <>
      <Spacer aria-hidden="true" data-testid="autojoin-spacer" />
      <Bar role="region" aria-label="Your CADINU profile" data-testid="autojoin">
        <Copy>
          {/* phones: the short title fits a 360 px bar next to the button (127 px of Figtree 700 14 px, measured on Stage) */}
          <Title>
            <span className="lg">Your CADINU profile is ready</span>
            <span className="sm">Your profile is ready</span>
          </Title>
          <Sub>
            <span className="lg">Next: claim it with one free signature. No transaction.</span>
            <span className="sm">Next: claim it, free</span>
          </Sub>
        </Copy>
        <Link href="/u" passHref legacyBehavior>
          <View data-testid="autojoin-view">View profile</View>
        </Link>
        <Close type="button" aria-label="Not now" title="Not now" onClick={dismiss}>
          ×
        </Close>
      </Bar>
    </>
  )
}
